import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { gatesAPI, gateSessionsAPI } from '../../services/api';
import { createPlayDedupe } from '../../utils/playDedupe';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || '';

const STATUS_COLORS = {
  idle: 'text-gray-400',
  ready: 'text-blue-400',
  playing: 'text-green-400',
  paused: 'text-yellow-400',
  error: 'text-red-400'
};

export default function GateDisplayPage() {
  const { gateId } = useParams();

  const [gate, setGate] = useState(null);
  const [session, setSession] = useState(null);
  const [playerStatus, setPlayerStatus] = useState('idle'); // idle | ready | playing | paused | error
  const [nowPlaying, setNowPlaying] = useState(null);
  const [queue, setQueue] = useState([]);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [connected, setConnected] = useState(false);
  const [lastHeartbeat, setLastHeartbeat] = useState(null);
  const [log, setLog] = useState([]);

  const socketRef = useRef(null);
  const audioRef = useRef(new Audio());
  const queueRef = useRef([]);
  const isPausedRef = useRef(false);
  const currentAnnIdRef = useRef(null);
  const playDedupeRef = useRef(createPlayDedupe());
  const suppressErrorRef = useRef(false); // set true when we intentionally clear audio.src
  const suppressEndedRef = useRef(false); // set true during audio unlock to ignore WAV end
  const audioUnlockedRef = useRef(false); // mirrors audioUnlocked state — readable inside stale closures

  const addLog = useCallback((msg) => {
    const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    setLog(prev => [`[${time}] ${msg}`, ...prev].slice(0, 30));
  }, []);

  // Fetch gate + active session on mount
  useEffect(() => {
    if (!gateId) return;
    gatesAPI.getById(gateId)
      .then(res => setGate(res.data.data))
      .catch(() => addLog('Failed to fetch gate info'));

    gateSessionsAPI.getAll({ gate_id: gateId, status: 'active' })
      .then(res => {
        const sessions = res.data.data;
        if (sessions?.length > 0) setSession(sessions[0]);
      })
      .catch(() => {});
  }, [gateId]);

  // Set up audio element handlers
  useEffect(() => {
    const audio = audioRef.current;

    const onEnded = () => {
      if (suppressEndedRef.current) {
        suppressEndedRef.current = false;
        return;
      }
      const annId = currentAnnIdRef.current;
      playDedupeRef.current.finish(annId);
      addLog(`Finished playing announcement ${annId}`);
      setPlayerStatus('ready');
      setNowPlaying(null);
      currentAnnIdRef.current = null;

      // Report played to server
      if (annId && socketRef.current) {
        socketRef.current.emit('announcement:played', { announcementId: annId, gateId });
      }

      // Play next in queue
      setTimeout(() => playNext(), 1500);
    };

    const onError = (e) => {
      // Ignore errors triggered by intentionally clearing audio.src
      if (suppressErrorRef.current) {
        suppressErrorRef.current = false;
        return;
      }
      addLog(`Audio error: ${e.target.error?.message || 'unknown'}`);
      setPlayerStatus('error');
      currentAnnIdRef.current = null;
      setTimeout(() => playNext(), 2000);
    };

    const onPlay = () => {
      setPlayerStatus('playing');
    };

    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    audio.addEventListener('play', onPlay);

    return () => {
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
      audio.removeEventListener('play', onPlay);
      audio.pause();
    };
  }, [gateId]);

  const playAudio = useCallback((audioUrl, announcementId, text, isAdhoc = false) => {
    // Use ref so the socket handler (set up once on mount) always reads the live value.
    if (!audioUnlockedRef.current) {
      addLog('Audio not yet unlocked — queuing');
      queueRef.current.push({ audioUrl, announcementId, text, isAdhoc });
      setQueue([...queueRef.current]);
      return;
    }

    if (isPausedRef.current && !isAdhoc) {
      queueRef.current.push({ audioUrl, announcementId, text, isAdhoc });
      setQueue([...queueRef.current]);
      addLog('Session paused — queued announcement');
      return;
    }

    // Never cut off an announcement mid-sentence — a PLAY that arrives while
    // audio is still going waits its turn (onEnded → playNext), same as the
    // device player. Only PLAY_ADHOC deliberately interrupts.
    if (!isAdhoc && !audioRef.current.paused && !audioRef.current.ended) {
      queueRef.current.push({ audioUrl, announcementId, text, isAdhoc });
      setQueue([...queueRef.current]);
      addLog(`Still playing — queued announcement ${announcementId}`);
      return;
    }

    const audio = audioRef.current;
    currentAnnIdRef.current = announcementId;

    const baseUrl = SOCKET_URL || window.location.origin;
    const fullUrl = audioUrl.startsWith('http') ? audioUrl : `${baseUrl}${audioUrl}`;

    audio.src = fullUrl;
    audio.load();
    setNowPlaying({ text, isAdhoc });
    setPlayerStatus('playing');

    if (announcementId && socketRef.current) {
      socketRef.current.emit('announcement:playing', { announcementId, gateId });
    }

    audio.play().catch(err => {
      addLog(`Playback failed: ${err.message}`);
      setPlayerStatus('error');
    });

    addLog(`Playing: ${isAdhoc ? '[AD-HOC] ' : ''}${text?.substring(0, 60) || audioUrl}`);
  }, [gateId]);

  const playNext = useCallback(() => {
    if (isPausedRef.current) return;
    if (queueRef.current.length === 0) {
      setPlayerStatus('ready');
      return;
    }
    const next = queueRef.current.shift();
    setQueue([...queueRef.current]);
    playAudio(next.audioUrl, next.announcementId, next.text, next.isAdhoc);
  }, [playAudio]);

  // Connect WebSocket and register as gate display
  useEffect(() => {
    if (!gateId) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 3000
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      socket.emit('gate:register', gateId);
      addLog(`Connected to server — registered as gate ${gateId}`);
    });

    socket.on('disconnect', () => {
      setConnected(false);
      addLog('Disconnected from server');
    });

    socket.on('gate:command', ({ command, payload }) => {
      addLog(`Received command: ${command}`);

      switch (command) {
        case 'PLAY': {
          const { audioUrl, announcementId, englishText, announcementType } = payload;
          if (!audioUrl) {
            addLog(`Skipping ann ${announcementId} — no audio file`);
            if (announcementId && socketRef.current) {
              socketRef.current.emit('announcement:played', { announcementId, gateId });
            }
            break;
          }
          // Same announcement re-sent (e.g. after a socket reconnect) — never restart it
          const seen = playDedupeRef.current.check(announcementId);
          if (seen === 'active') { addLog(`Ignoring repeat PLAY for ${announcementId} — already playing/queued`); break; }
          if (seen === 'finished') {
            addLog(`Ignoring repeat PLAY for ${announcementId} — already played; re-reporting`);
            socketRef.current?.emit('announcement:played', { announcementId, gateId });
            break;
          }
          playDedupeRef.current.accept(announcementId);
          playAudio(audioUrl, announcementId, englishText || announcementType);
          break;
        }
        case 'PLAY_ADHOC': {
          const { audioUrl, englishText, title } = payload;
          if (!audioUrl) {
            addLog(`PLAY_ADHOC skipped — no audio file`);
            break;
          }
          // Ad-hoc interrupts queue — play immediately
          const audio = audioRef.current;
          if (!audio.paused) {
            audio.pause();
            audio.currentTime = 0;
          }
          queueRef.current = [];
          setQueue([]);
          playAudio(audioUrl, null, englishText || title, true);
          break;
        }
        case 'PAUSE': {
          // Emergency Pause (CR 6.1-6.2): hard-stop immediately, even mid-playback —
          // do NOT let it finish, and do NOT just native-pause (that would let Resume
          // continue from the same audio position, which CR 6.3 explicitly forbids).
          isPausedRef.current = true;
          const audio = audioRef.current;
          const wasPlaying = !audio.paused;
          const annId = currentAnnIdRef.current;
          audio.pause();
          audio.currentTime = 0;
          // Resume re-sends the interrupted announcement (same id) as a fresh
          // PLAY — let it, and anything that was queued, through again
          playDedupeRef.current.release(annId);
          queueRef.current.forEach(q => playDedupeRef.current.release(q.announcementId));
          queueRef.current = [];
          setQueue([]);
          setNowPlaying(null);
          currentAnnIdRef.current = null;
          setPlayerStatus('paused');
          addLog(wasPlaying && annId
            ? `Emergency pause — announcement ${annId} interrupted`
            : 'Emergency pause — nothing was playing');
          break;
        }
        case 'RESUME': {
          // The server already resets the interrupted announcement to a fresh
          // 'ready' item with a new scheduled_time and shifts the rest — the
          // normal scheduler dispatch sends a standard PLAY for it next, so
          // there is nothing to resume locally, just unpause the dispatch gate.
          isPausedRef.current = false;
          setPlayerStatus('ready');
          addLog('Session resumed — waiting for next scheduled announcement');
          break;
        }
        case 'SKIP': {
          const audio = audioRef.current;
          if (!audio.paused) {
            audio.pause();
            audio.currentTime = 0;
            const annId = currentAnnIdRef.current;
            currentAnnIdRef.current = null;
            setNowPlaying(null);
            if (annId && socket) {
              socket.emit('announcement:played', { announcementId: annId, gateId });
            }
            setTimeout(() => playNext(), 500);
          }
          addLog('Announcement skipped');
          break;
        }
        case 'CANCEL_SESSION': {
          const audio = audioRef.current;
          audio.pause();
          suppressErrorRef.current = true;
          audio.src = '';
          queueRef.current = [];
          setQueue([]);
          setNowPlaying(null);
          setPlayerStatus('idle');
          setSession(null);
          currentAnnIdRef.current = null;
          addLog('Session cancelled — player idle');
          break;
        }
        default:
          addLog(`Unknown command: ${command}`);
      }
    });

    // Update local session state from session events
    socket.on('session:created', (data) => {
      setSession(data);
      setPlayerStatus('ready');
      addLog(`New session started: flight ${data.flight_number}`);
    });

    socket.on('session:status', ({ status }) => {
      if (status === 'completed') {
        setPlayerStatus('idle');
        setSession(prev => prev ? { ...prev, status } : prev);
        addLog('Session completed');
      }
    });

    // Heartbeat every 30 seconds
    const heartbeatInterval = setInterval(() => {
      if (socket.connected) {
        socket.emit('gate:heartbeat', {
          gateId,
          status: playerStatus,
          queueLength: queueRef.current.length
        });
        setLastHeartbeat(new Date());
      }
    }, 30000);

    return () => {
      clearInterval(heartbeatInterval);
      socket.disconnect();
    };
  }, [gateId]);

  // Re-wire playAudio/playNext into gate:command handler when audioUnlocked changes
  useEffect(() => {
    if (audioUnlocked && queueRef.current.length > 0) {
      addLog('Audio unlocked — processing queued announcements');
      playNext();
    }
  }, [audioUnlocked]);

  function unlockAudio() {
    const audio = audioRef.current;
    suppressEndedRef.current = true; // silent WAV may end before we can pause it
    // Play silent audio to satisfy browser autoplay policy
    audio.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
    audio.play()
      .then(() => {
        audio.pause();
        // Do NOT clear audio.src — clearing it triggers a spurious MEDIA_ELEMENT_ERROR.
        // The tiny silent WAV stays loaded until the next real audio overwrites it.
        audioUnlockedRef.current = true; // update ref first so any queued playAudio calls see it
        setAudioUnlocked(true);
        setPlayerStatus('ready');
        addLog('Audio context unlocked');
      })
      .catch(err => addLog(`Failed to unlock audio: ${err.message}`));
  }

  const statusColor = STATUS_COLORS[playerStatus] || 'text-gray-400';

  return (
    <div className="min-h-screen bg-gray-950 text-white font-mono flex flex-col">
      {/* Header bar */}
      <div className="bg-gray-900 border-b border-gray-800 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400 animate-pulse' : 'bg-red-500'}`} />
          <span className="text-sm text-gray-400">{connected ? 'CONNECTED' : 'DISCONNECTED'}</span>
        </div>
        <div className="text-xs text-gray-500">
          {lastHeartbeat ? `Last heartbeat: ${lastHeartbeat.toLocaleTimeString()}` : 'No heartbeat yet'}
        </div>
      </div>

      {/* Main display */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 gap-6">
        {/* Gate code */}
        <div className="text-center">
          <div className="text-7xl font-black text-white tracking-widest mb-1">
            {gate?.gate_code || gateId || '--'}
          </div>
          <div className="text-sm text-gray-500 uppercase tracking-widest">
            {gate?.terminal ? `Terminal ${gate.terminal}` : 'Gate Display'}
          </div>
        </div>

        {/* Flight info */}
        {session ? (
          <div className="bg-gray-900 border border-gray-700 rounded-2xl px-8 py-5 text-center min-w-[280px]">
            <div className="text-2xl font-bold text-blue-400 mb-1">{session.flight_number}</div>
            <div className="text-gray-300 text-sm">{session.airline}</div>
            <div className="text-gray-400 text-sm mt-1">
              {session.destination} {session.destination_code ? `(${session.destination_code})` : ''}
            </div>
            <div className="text-gray-500 text-xs mt-2">
              Dep: {session.departure_time ? new Date(session.departure_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--'}
            </div>
          </div>
        ) : (
          <div className="text-gray-600 text-sm uppercase tracking-widest">No active session</div>
        )}

        {/* Player status */}
        <div className="text-center">
          <div className={`text-lg font-semibold uppercase tracking-widest ${statusColor}`}>
            {playerStatus}
          </div>
          {nowPlaying && (
            <div className="mt-2 max-w-md text-sm text-gray-300 text-center leading-relaxed">
              {nowPlaying.isAdhoc && (
                <span className="inline-block bg-orange-900 text-orange-300 text-xs px-2 py-0.5 rounded-full mr-2 mb-1">AD-HOC</span>
              )}
              "{nowPlaying.text}"
            </div>
          )}
        </div>

        {/* Queue indicator */}
        {queue.length > 0 && (
          <div className="text-xs text-gray-500">
            {queue.length} announcement{queue.length !== 1 ? 's' : ''} queued
          </div>
        )}

        {/* Audio unlock button */}
        {!audioUnlocked && (
          <button
            onClick={unlockAudio}
            className="mt-4 px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-2xl text-lg transition-colors shadow-lg animate-pulse"
          >
            Tap to Activate Audio
          </button>
        )}
      </div>

      {/* Activity log */}
      <div className="bg-gray-900 border-t border-gray-800 px-4 py-3 max-h-40 overflow-y-auto">
        <div className="text-xs text-gray-600 uppercase tracking-wider mb-2">Activity Log</div>
        {log.length === 0 ? (
          <div className="text-xs text-gray-700">No activity yet</div>
        ) : (
          log.map((entry, i) => (
            <div key={i} className="text-xs text-gray-400 leading-5">{entry}</div>
          ))
        )}
      </div>
    </div>
  );
}
